import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, Subscription, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

// Background reads (user data polling etc.) give up after this time; they never block the UI.
export const GET_TIMEOUT_MS = 30000;

// Quiz steps are never aborted while they may still complete (on a lossy HTTP/2 connection a
// single request can take minutes, and aborting it only starts it over). After QUIZ_HEDGE_MS one
// identical request is sent in parallel and the first response wins; the backend treats these
// calls as idempotent. The extra request also makes Chromium notice a dead HTTP/2 session.
export const QUIZ_HEDGE_MS = 12000;
export const QUIZ_HARD_TIMEOUT_MS = 600000;
const QUIZ_STEP_URL = /\/(quiz-code\/[^/]+|user-quiz-attempt\/\d+\/(rate|answer|activity-quiz))$/;

@Injectable()
export class QuizRequestInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.method === 'POST' && QUIZ_STEP_URL.test(req.url.split('?')[0])) {
      return this.hedged(req, next);
    }

    if (req.method === 'GET') {
      return next.handle(req).pipe(
        timeout(GET_TIMEOUT_MS),
        catchError(error => throwError(() => error instanceof TimeoutError ? this.timeoutError(req) : error)),
      );
    }

    return next.handle(req);
  }

  private hedged(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return new Observable<HttpEvent<unknown>>(observer => {
      const attempts: Subscription[] = [];
      let failed = 0;
      let finished = false;

      const launch = () => {
        const index = attempts.length;
        attempts.push(next.handle(req).subscribe({
          next: event => {
            if (finished || !(event instanceof HttpResponse)) {
              return;
            }
            finished = true;
            attempts.forEach((attempt, i) => i !== index && attempt.unsubscribe());
            observer.next(event);
            observer.complete();
          },
          error: error => {
            failed++;
            if (!finished && failed === attempts.length) {
              finished = true;
              observer.error(error);
            }
          },
        }));
      };

      launch();
      const hedgeTimer = setTimeout(() => !finished && launch(), QUIZ_HEDGE_MS);
      const hardTimer = setTimeout(() => {
        if (!finished) {
          finished = true;
          attempts.forEach(attempt => attempt.unsubscribe());
          observer.error(this.timeoutError(req));
        }
      }, QUIZ_HARD_TIMEOUT_MS);

      return () => {
        clearTimeout(hedgeTimer);
        clearTimeout(hardTimer);
        attempts.forEach(attempt => attempt.unsubscribe());
      };
    });
  }

  private timeoutError(req: HttpRequest<unknown>): HttpErrorResponse {
    return new HttpErrorResponse({ status: 0, statusText: 'Timeout', url: req.url });
  }
}
